import { useAppSelector } from "../../../store/hooks"
import {
    DEFAULT_THREAD_PARAMS,
    messagingApi,
} from "../../../store/messagingSlice"
import type { RootState } from "../../../store/store"
import type { Thread } from "../types"

/**
 * Find a thread in both the default and current search caches.
 * Checks the default inbox cache first, then falls back to current search term cache if active.
 * @param targetUsername The username of the thread to find
 * @returns The found thread, or undefined if not found in either cache
 */
export const useFindThreadInCaches = (
    targetUsername: string,
): Thread | undefined => {
    const currentSearchUsername = useAppSelector(
        (state: RootState) => state.messaging.currentSearchUsername,
    )

    const defaultCacheData = useAppSelector(
        (state: RootState) =>
            messagingApi.endpoints.getThreads.select(DEFAULT_THREAD_PARAMS)(
                state,
            )?.data,
    )
    const searchCacheData = useAppSelector((state: RootState) =>
        currentSearchUsername
            ? messagingApi.endpoints.getThreads.select({
                  offset: 0,
                  search_username: currentSearchUsername,
              })(state)?.data
            : undefined,
    )

    const defaultThread = defaultCacheData?.threads?.find(
        (t: Thread) => t?.other_user?.username === targetUsername,
    )

    if (defaultThread) {
        return defaultThread
    }

    if (currentSearchUsername) {
        const searchThread = searchCacheData?.threads?.find(
            (t: Thread) => t?.other_user?.username === targetUsername,
        )
        if (searchThread) {
            return searchThread
        }
    }

    return undefined
}
